/**
 * Plantillas de patrones laborales (equivalente a ejemplos Postman).
 * Cada plantilla define metadatos del patrón y la lista de días para days-add.
 */

export const PATTERN_TEMPLATE_IDS = {
  ROTATIVE_3X8: 'rotative_3x8',
  FIXED_ADMIN: 'fixed_admin',
  ALTERNATE_2X8: 'alternate_2x8',
  ALTERNATE_WEEKEND_2X12: 'alternate_weekend_2x12',
};

export const PATTERN_TEMPLATES = [
  {
    id: PATTERN_TEMPLATE_IDS.ROTATIVE_3X8,
    label: 'Rotativo 3×8 (21 días)',
    description: 'Mañana lun–vie, tarde semana 2, noche semana 3. Ciclo 21 días.',
    defaultName: 'Rotativo 3x8',
    defaultCodePrefix: 'ROT-3X8',
    isRotating: true,
    cycleDays: 21,
    workingDayCount: 15,
  },
  {
    id: PATTERN_TEMPLATE_IDS.FIXED_ADMIN,
    label: 'Fijo administración (lun–vie)',
    description: 'Jornada partida 08:00–17:00, mismo horario cada semana.',
    defaultName: 'Administración jornada partida',
    defaultCodePrefix: 'ADM-PARTIDA',
    isRotating: false,
    cycleDays: 0,
    workingDayCount: 5,
  },
  {
    id: PATTERN_TEMPLATE_IDS.ALTERNATE_2X8,
    label: 'Alterno 2×8 (14 días)',
    description: 'Semana 1 mañana, semana 2 tarde. Ciclo 14 días.',
    defaultName: 'Alterno 2x8',
    defaultCodePrefix: 'ALT-2X8',
    isRotating: true,
    cycleDays: 14,
    workingDayCount: 10,
  },
  {
    id: PATTERN_TEMPLATE_IDS.ALTERNATE_WEEKEND_2X12,
    label: 'Alterno fin de semana 2×12',
    description: 'Solo fines de semana del ciclo (días 5–6 y 12–13). Ciclo 14 días.',
    defaultName: 'Alterno fin de semana',
    defaultCodePrefix: 'ALT-FS-2X12',
    isRotating: true,
    cycleDays: 14,
    workingDayCount: 4,
  },
];

const WEEKDAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function dayRotating(seq, shiftCode, times) {
  return {
    day_sequence: seq,
    day_of_week: null,
    shift_code: shiftCode,
    is_working_day: true,
    ...times,
  };
}

function dayFixed(dayOfWeek, daySequence, shiftCode, times) {
  return {
    day_sequence: daySequence,
    day_of_week: dayOfWeek,
    shift_code: shiftCode,
    is_working_day: true,
    ...times,
  };
}

function buildRotative3x8Days() {
  const morning = {
    start_time: '06:00',
    end_time: '14:00',
    break_minutes: 15,
    total_hours: 7.75,
  };
  const afternoon = {
    start_time: '14:00',
    end_time: '22:00',
    break_minutes: 15,
    total_hours: 7.75,
  };
  const night = {
    start_time: '22:00',
    end_time: '06:00',
    break_minutes: 15,
    total_hours: 7.75,
  };
  const days = [];
  for (let s = 0; s <= 4; s++) days.push(dayRotating(s, 'morning', morning));
  for (let s = 7; s <= 11; s++) days.push(dayRotating(s, 'afternoon', afternoon));
  for (let s = 14; s <= 18; s++) days.push(dayRotating(s, 'night', night));
  return days;
}

function buildFixedAdminDays() {
  const times = {
    start_time: '08:00',
    end_time: '17:00',
    break_minutes: 60,
    total_hours: 8,
  };
  return [1, 2, 3, 4, 5].map((dow, i) =>
    dayFixed(dow, i, 'Administracion Jornada Partida', times)
  );
}

function buildAlternate2x8Days() {
  const morning = {
    start_time: '07:00',
    end_time: '15:00',
    break_minutes: 15,
    total_hours: 7.75,
  };
  const afternoon = {
    start_time: '15:00',
    end_time: '23:00',
    break_minutes: 15,
    total_hours: 7.75,
  };
  const days = [];
  for (let s = 0; s <= 4; s++) days.push(dayRotating(s, 'Alterno Mañana', morning));
  for (let s = 7; s <= 11; s++) days.push(dayRotating(s, 'Alterno Tarde', afternoon));
  return days;
}

function buildAlternateWeekendDays() {
  const morning = {
    start_time: '06:00',
    end_time: '18:00',
    break_minutes: 30,
    total_hours: 11.5,
  };
  const afternoon = {
    start_time: '18:00',
    end_time: '06:00',
    break_minutes: 30,
    total_hours: 11.5,
  };
  const days = [];
  for (let s = 5; s <= 6; s++) days.push(dayRotating(s, 'Fin Semana Mañana', morning));
  for (let s = 12; s <= 13; s++) days.push(dayRotating(s, 'Fin Semana Tarde', afternoon));
  return days;
}

/** Devuelve los días del patrón según plantilla */
export function buildPatternDaysForTemplate(templateId) {
  switch (templateId) {
    case PATTERN_TEMPLATE_IDS.ROTATIVE_3X8:
      return buildRotative3x8Days();
    case PATTERN_TEMPLATE_IDS.FIXED_ADMIN:
      return buildFixedAdminDays();
    case PATTERN_TEMPLATE_IDS.ALTERNATE_2X8:
      return buildAlternate2x8Days();
    case PATTERN_TEMPLATE_IDS.ALTERNATE_WEEKEND_2X12:
      return buildAlternateWeekendDays();
    default:
      return [];
  }
}

export function getPatternTemplate(templateId) {
  return PATTERN_TEMPLATES.find((t) => t.id === templateId) || PATTERN_TEMPLATES[0];
}

/** Primer lunes del año (referencia habitual para ciclo rotativo) */
export function defaultCycleStartForYear(year) {
  const d = new Date(year, 0, 1);
  while (d.getDay() !== 1) {
    d.setDate(d.getDate() + 1);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Código único sugerido */
export function suggestPatternCode(prefix) {
  const suffix = Date.now().toString(36).slice(-4).toUpperCase();
  return `${prefix}-${suffix}`;
}

/** Resumen legible de días a crear */
export function describePatternDays(templateId, days) {
  const tpl = getPatternTemplate(templateId);
  if (tpl.id === PATTERN_TEMPLATE_IDS.FIXED_ADMIN) {
    const labels = days.map((d) => WEEKDAY_LABELS[d.day_of_week] ?? d.day_of_week);
    return `${days.length} días laborables (${labels.join(', ')})`;
  }
  const seqs = days.map((d) => d.day_sequence).sort((a, b) => a - b);
  return `${days.length} días en ciclo de ${tpl.cycleDays} (secuencias ${seqs.join(', ')})`;
}
